import type { Building } from "building-types/building/buildingTypes";
import type { RemoveFormula } from "building-types/options/formula";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofDormerPlacement, SkylightPlacement } from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolume } from "building-types/roofVolumes/roofVolumeTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import type { RWTable, Table } from "common/data/table";
import deepequal from "common/utils/deepequal";
import objectFilter from "common/utils/objectKeyFilter";
import objectMap from "common/utils/objectMap";
import shallowCompare from "common/utils/shallowCompare";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { polyLine2Compare } from "geom/polyLine2";
import { evaluateRoofSystems } from "../elements/roofs/evaluateRoofSystems";
import { makeRoofSystemsVolumesLegacy } from "../elements/roofVolumes/generateLegacy/roofVolumeLegacy";
import { addRoofComponentInstances } from "../generationTools/components/componentRoofs";
import { makeRoofVolume } from "../generationTools/generateRoofVolumesTools";
import memo from "../utils/memo";

function select({
  settings,
  doc: {
    optionSets,
    instanceOptionSets,
    roofVolumes,
    roofSystems,
    roofs,
    roofOpenings,
    dormers,
    skylights,
    edgeModifiers,
    entitySettings,
    settings: buildingDocSettings,
  },
  windowPlacements: windows,
  windowUnits,
  windowShutters,
  optionChoices,
  entityDisabledByGroup,
  blocks,
  blockPerimeters,
  mirror,
  mirrorTranslationX,
  levels,
  modifiers,
  modifiersByInstanceChain,
  settingsSets: activeSettingsSets,
  rooms,
  roomFloorAssemblies,
  roomLevelIndices,
  planarGraphs,
  wallMasses,
  enabledFeatures,
  isolatedEntity,
  idToParents,
  componentToTransforms,
  wallPlaneMasses,
  buildingGraph,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    {
      roofSettings: settings.roof,
      dormerSettings: settings.dormer,
      roofFramingSettings: settings.roofFraming,
      dormers, //TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers change
      skylights, //TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when skylights change
      roofSystems,
      roofs,
      roofOpenings,
    },
    definitions,
    optionChoices,
  );

  const blockLevelIds = objectFilter(
    objectMap(blocks, (x) => x.levelId),
    (x) => !!blockPerimeters[x],
  );

  const roofSettings = evalFormulas(
    settings.roof,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as RemoveFormula<RoofSettings>;
  const roofFramingSettings = evalFormulas(
    settings.roofFraming,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as RoofFramingSettings;
  const dormerSettings = evalFormulas(
    settings.dormer,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as DormerSettings;

  const blockIsHalfStory: Table<boolean> = objectMap(blocks, (x) => !!x.isHalfStory);

  //TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers and skylight change
  const dormersFiltered = evalFormulas(
    dormers,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as Table<RoofDormerPlacement>;
  const skylightsFiltered = evalFormulas(
    skylights,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as Table<SkylightPlacement>;

  return {
    roofSystems,
    roofs,
    roofVolumes,
    resolvedOptionChoices,
    entityDisabledByGroup,
    wallSettings: settings.wall,
    roofSettings,
    roofFramingSettings,
    dormerSettings,
    levels,
    roofOpenings, // TODO: Dormers roof volume should be generated in a separate stage. Remove once that is done
    windows, // TODO: Dormers roof volume should be generated in a separate stage, not using windows as input. Remove once that is done
    windowUnits, // TODO: Dormers roof volume should be generated in a separate stage, not using windows as input. Remove once that is done
    windowShutters, // TODO: Dormers roof volume should be generated in a separate stage, not using windows as input. Remove once that is done
    blockPerimeters,
    blockLevelIds,
    blockIsHalfStory,
    rooms,
    roomFloorAssemblies,
    roomLevelIndices,
    polygonPlanarGraphs: planarGraphs,
    mirror,
    mirrorTranslationX,
    edgeModifiers,
    modifiersByInstanceChain,
    buildingDocSettings,
    entitySettings,
    activeSettingsSets,
    modifiers,
    wallMasses,
    dormers: dormersFiltered, // TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers change
    skylights: skylightsFiltered, // TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when skylights change
    optionChoices, // TODO: This has been added to trigger this generation stage in parity with generateRoofSystemClipping. Find a cleaner solution
    definitions,
    isolatedEntity,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    wallPlaneMasses,
    buildingGraph,
  };
}

function compare(a: ReturnType<typeof select>, b: ReturnType<typeof select>) {
  if (!a || !b) {
    return false;
  }

  for (const k in a) {
    if (k === "blockPerimeters") {
      const aa = a[k];
      const bb = b[k];
      const oldPropNames = Object.getOwnPropertyNames(aa);
      const newPropNames = Object.getOwnPropertyNames(bb);

      if (oldPropNames.length !== newPropNames.length) {
        return false;
      }

      for (const blockId of oldPropNames) {
        if (!aa[blockId]) {
          return false;
        }
        if (!bb[blockId]) {
          return false;
        }

        if (aa[blockId].length !== bb[blockId].length) {
          return false;
        }

        for (let i = 0; i < aa[blockId].length; i++) {
          if (!polyLine2Compare(aa[blockId][i], bb[blockId][i])) {
            return false;
          }
        }
      }
    } else if (k === "optionChoices") {
      if (!shallowCompare(a[k], b[k])) {
        return false;
      }
    } else if (!deepequal(a[k], b[k])) {
      return false;
    }
  }

  return true;
}

function generate({
  roofSystems,
  roofs,
  levels,
  blockPerimeters,
  blockLevelIds,
  mirror,
  mirrorTranslationX,
  wallSettings,
  roofSettings,
  dormerSettings,
  roofFramingSettings,
  roofVolumes,
  resolvedOptionChoices,
  entityDisabledByGroup,
  edgeModifiers,
  modifiersByInstanceChain,
  buildingDocSettings,
  entitySettings,
  activeSettingsSets,
  modifiers,
  blockIsHalfStory,
  rooms,
  roomFloorAssemblies,
  roomLevelIndices,
  polygonPlanarGraphs,
  wallMasses,
  isolatedEntity,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  definitions,
  optionChoices,
  wallPlaneMasses,
  buildingGraph,
}: ReturnType<typeof select>): Pick<
  Building,
  "roofVolumes" | "roofSystemLevels" | "roofSystemClipContexts" | "issues"
> {
  const generatedRoofVolumes: RWTable<GeneratedRoofVolume> = {};
  const roofSystemLevels: RWTable<RoofSystemLevel> = {};
  const roofSystemClipContexts: RWTable<MultiGableRoofClipContext> = {};
  const issues: BuildingGenerationIssue[] = [];

  let { roofSystems: evaluatedRoofSystems, roofs: evaluatedRoofs } = evaluateRoofSystems({
    initialRoofSystems: roofSystems,
    initialRoofs: roofs,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
  });

  if (enabledFeatures?.modelComponents) {
    const augmented = addRoofComponentInstances(
      evaluatedRoofSystems,
      evaluatedRoofs,
      roofSystems,
      roofs,
      idToParents,
      componentToTransforms,
      levels,
      isolatedEntity,
      {},
      {},
      definitions,
      optionChoices,
    );
    evaluatedRoofSystems = augmented.roofSystems;
    evaluatedRoofs = augmented.roofs;
  }

  // legacy roof volumes
  makeRoofSystemsVolumesLegacy({
    roofSystems: evaluatedRoofSystems,
    roofs: evaluatedRoofs,
    levels,
    blockPerimeters,
    blockLevelIds,
    mirror,
    mirrorTranslationX,
    wallSettings,
    roofSettings,
    dormerSettings,
    roofFramingSettings,
    resolvedOptionChoices,
    edgeModifiers,
    modifiersByInstanceChain,
    settings: buildingDocSettings,
    entitySettings,
    activeSettingsSets,
    modifiers,
    idToParents,
    blockIsHalfStory,
    rooms,
    roomFloorAssemblies,
    roomLevelIndices,
    polygonPlanarGraphs,
    wallMasses,
    generatedRoofVolumes,
    roofSystemLevels,
    roofSystemClipContexts,
    issues,
    isolatedEntity,
    wallPlanes: enabledFeatures.wallPlanes ?? false,
    wallPlaneMasses: enabledFeatures.wallPlanes ? wallPlaneMasses : undefined,
    buildingGraph: enabledFeatures.wallPlanes ? buildingGraph : undefined,
  });

  // new roof volumes
  if (!roofVolumes) {
    return { roofVolumes: generatedRoofVolumes, roofSystemLevels, roofSystemClipContexts, issues };
  }

  roofVolumes = evalFormulas(roofVolumes, (v) => resolvedOptionChoices[v], undefined, entityDisabledByGroup);

  for (const tableId in roofVolumes) {
    const roofVolume = roofVolumes[tableId];

    if (roofVolume.disabled) {
      continue;
    }

    generatedRoofVolumes[tableId] = makeRoofVolume(roofVolume);
  }

  return { roofVolumes: generatedRoofVolumes, roofSystemLevels, roofSystemClipContexts, issues };
}

export default memo(select, generate, compare);
